import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'

import { AuthCookieService } from '../../utils/AuthCookieService'
import { handleOperationError } from './errorHandler'

if (import.meta.env.DEV) {
  loadDevMessages()
  loadErrorMessages()
}

export type Client = ApolloClient

const getApolloClient = (): ApolloClient => {
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          projectFolderUserACL: { merge: false },
          projectFolderCompanyACL: { merge: false },
        },
      },
      Project: {
        fields: {
          userACL: { merge: false },
          allUserACL: { merge: false },
        },
      },
      User: {
        fields: {
          organizationACL: { merge: false },
          projectACL: { merge: false },
        },
      },
      // Form field models must NOT be normalized by their id: a scheduled form and the
      // FormModel template it was created from can share field ids, so global normalization
      // lets an empty template (e.g. loaded by availableFormModels) overwrite the saved
      // values of a filled form. keyFields: false embeds each field inside its parent
      // (Form / FormModel), keeping their values isolated. Array columns, grid rows and
      // options carry ids copied the same way and hold nested values, so they are embedded too.
      ArrayColModel: { keyFields: false },
      GridRowModel: { keyFields: false },
      OptionModel: { keyFields: false },
      PictureFieldModel: {
        keyFields: false,
        fields: { files: { merge: false } },
      },
      FileFieldModel: {
        keyFields: false,
        fields: { files: { merge: false } },
      },
      FixedPictureFieldModel: {
        keyFields: false,
        fields: { files: { merge: false } },
      },
      FixedFileFieldModel: {
        keyFields: false,
        fields: { files: { merge: false } },
      },
      ArrayFieldModel: { keyFields: false },
      CheckboxFieldModel: { keyFields: false },
      CodificationFixedTextFieldModel: { keyFields: false },
      CodificationSelectFieldModel: { keyFields: false },
      CodificationTextFieldModel: { keyFields: false },
      ConformityFieldModel: { keyFields: false },
      DateFieldModel: { keyFields: false },
      GridTextFieldModel: { keyFields: false },
      HeaderPictureFieldModel: { keyFields: false },
      RadioFieldModel: { keyFields: false },
      SectionFieldModel: { keyFields: false },
      SelectEditableFieldModel: { keyFields: false },
      SelectFieldModel: { keyFields: false },
      SignatureFieldModel: { keyFields: false },
      TextFieldModel: { keyFields: false },
      TitleFieldModel: { keyFields: false },
      ProjectFile: {
        fields: { codificationValues: { merge: false } },
      },
    },
  })

  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_API_GRAPHQL_ENDPOINT,
  })

  const authLink = new SetContextLink(async ({ headers }) => {
    const authInfo = await AuthCookieService.getAuthInfo()
    const token = authInfo?.accessToken

    return {
      headers: token ? { ...headers, Authorization: `Bearer ${token}` } : headers,
    }
  })

  const errorLink = new ErrorLink(handleOperationError)

  return new ApolloClient({
    cache,
    link: ApolloLink.from([errorLink, authLink, httpLink]),
  })
}

export const apolloClient = getApolloClient()
